import AddCircleIcon from "@material-ui/icons/AddCircle";
import PropTypes from "prop-types";
import { toCamelCase } from "../../util/text";
import { customerPalette } from "../../theme/theme";
import Money from "../money";
import AccessoryDetails from "./AccessoryDetails";

export const addons = Object.seal(
  Object.freeze({
    BLACK_CARDBOARD_FRAME: "black-cardboard-frame",
    COA_ADDON: "coa-addon",
    FRAME_ADDON: "frame-addon",
    HARD_SHELL_CASE: "hard-shell-case",
    MAGNET_HOLDER: "magnet-holder",
    SOFT_SHELL_CASE: "soft-shell-case",
    TOP_LOADER: "top-loader",
    VIDEO_ADDON: "video-addon",
    VIDEO_MESSAGE_ADDON: "video-message-addon",
    VIP_ADDON: "vip-addon",
  }),
);

export const getters = Object.fromEntries(
  Object.values(addons).map((name) => [
    toCamelCase(name),
    (addonMap = {}) =>
      Object.values(addonMap).find(
        (addon) => addon.name === name || addon.addon_name === name,
      ),
  ]),
);

export const additionalDetailsFor = (addon) => {
  if (addon.image_url) {
    return <AccessoryDetails imageUrl={addon.image_url} />;
  }

  return null;
};

export function toDomainAddon(addon) {
  return {
    isFrameAddon: addon.addon_name === addons.FRAME_ADDON,
    isCOAAddon: addon.addon_name === addons.COA_ADDON,
    isVideoAddon: addon.addon_name === addons.VIDEO_ADDON,
    isVideoMessageAddon: addon.addon_name === addons.VIDEO_MESSAGE_ADDON,
    isVipAddon: addon.addon_name === addons.VIP_ADDON,
    isTopLoader: addon.addon_name === addons.TOP_LOADER,
    isSoftShellCase: addon.addon_name === addons.SOFT_SHELL_CASE,
    isHardShellCase: addon.addon_name === addons.HARD_SHELL_CASE,
    isBlackCardboardFrame: addon.addon_name === addons.BLACK_CARDBOARD_FRAME,
    isMagnetHolder: addon.addon_name === addons.MAGNET_HOLDER,

    name: addon.addon_name,
    title: addon.addon_title,
    href: addon.href,
    description: addon.addon_description,
    icon: addon.addon_icon ? (
      <div
        style={{ height: 24, width: 24 }}
        dangerouslySetInnerHTML={{ __html: addon.addon_icon }}
      />
    ) : (
      <AddCircleIcon />
    ),
    color: addon.addon_color ?? customerPalette.primaryDark,
    price: Money.for({ value: addon.addon_price / 100, currency: "usd" }),
    rawPrice: addon.addon_price ?? 0,
    priceMultiplier: addon.addon_price_multiplier ?? 1,
    shippingBump: Money.for({
      value: addon.addon_shipping_bump / 100,
      currency: "usd",
    }),
    isActive: Boolean(addon.active),
    created: addon.created ? new Date(addon.created) : null,
    updated: addon.updated ? new Date(addon.updated) : null,
    autographPurchaseId: addon.autograph_purchase_id,
    tax: Money.for({ value: addon.addon_tax / 100, currency: "usd" }),
    total: Money.for({ value: addon.addon_total / 100, currency: "usd" }),
    purchaseLocation: addon.purchase_location,
    paymentIntentId: addon.payment_intent_id,
    isRefunded: Boolean(addon.refunded),
    isNew: Boolean(addon.is_new),
    additionalDetails: additionalDetailsFor(addon),
    clone(mixin = {}) {
      return toDomainAddon({ ...addon, ...mixin });
    },
  };
}

export const addonPropType = PropTypes.shape({
  isFrameAddon: PropTypes.bool.isRequired,
  isCOAAddon: PropTypes.bool.isRequired,
  isVideoAddon: PropTypes.bool.isRequired,
  isVideoMessageAddon: PropTypes.bool.isRequired,
  isVipAddon: PropTypes.bool.isRequired,
  isTopLoader: PropTypes.bool.isRequired,
  isSoftShellCase: PropTypes.bool.isRequired,
  isHardShellCase: PropTypes.bool.isRequired,
  isBlackCardboardFrame: PropTypes.bool.isRequired,
  isMagnetHolder: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  description: PropTypes.string,
  icon: PropTypes.element.isRequired,
  price: PropTypes.instanceOf(Money).isRequired,
  rawPrice: PropTypes.number.isRequired,
  priceMultiplier: PropTypes.number.isRequired,
  shippingBump: PropTypes.instanceOf(Money).isRequired,
  isActive: PropTypes.bool.isRequired,
  created: PropTypes.instanceOf(Date),
  updated: PropTypes.instanceOf(Date),
  autographPurchaseId: PropTypes.number,
  tax: PropTypes.instanceOf(Money).isRequired,
  total: PropTypes.instanceOf(Money).isRequired,
  purchaseLocation: PropTypes.string,
  paymentIntentId: PropTypes.string,
  isRefunded: PropTypes.bool.isRequired,
  isNew: PropTypes.bool.isRequired,
  additionalDetails: PropTypes.element,
});
